import { useState, useEffect, useRef } from 'react';

import styled, { keyframes, css } from 'styled-components';

import { useTheme } from '../../Context/ThemeContext';
import { useLanguage } from '../../Context/LanguageContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

const CONFETTI_COLORS = ['#FF6B6B', '#FFD93D', '#6BCB77', '#4D96FF', '#C77DFF', '#FF9F43'];

function generateConfetti() {
  return Array.from({ length: 18 }, (_, i) => ({
    id: i,
    x: Math.random() * 44 - 8,
    y: Math.random() * 10 - 24,
    color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
    width: 4 + Math.random() * 4,
    height: 3 + Math.random() * 3,
    rotation: Math.random() * 360,
    delay: Math.random() * 0.25,
  }));
}

export default function RatingBar(props) {
  const { rating, onRate } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const { t } = useLanguage();

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isPanelMounted, setIsPanelMounted] = useState(false);
  const [isThanksVisible, setIsThanksVisible] = useState(false);
  const [isThanksTextMounted, setIsThanksTextMounted] = useState(false);

  const [selectedReason, setSelectedReason] = useState(null);
  const [commentValue, setCommentValue] = useState('');

  const [isWobbling, setIsWobbling] = useState(false);
  const [confetti, setConfetti] = useState([]);

  const negativeReasons = [
    t('aiManager.rating.reasonInaccurate'),
    t('aiManager.rating.reasonWrongIntent'),
    t('aiManager.rating.reasonSlow'),
    t('aiManager.rating.reasonBadData'),
  ];

  const ThanksTimerRef = useRef(null);
  const WobbleTimerRef = useRef(null);
  const ConfettiTimerRef = useRef(null);
  const FeedbackPanelRef = useRef(null);

  // isPanelOpen true 시 DOM 마운트, false 시 close 애니메이션 후 언마운트 //
  useEffect(() => {
    if (isPanelOpen) setIsPanelMounted(true);
  }, [isPanelOpen]);

  // isPanelMounted 후 실제 DOM commit 시점에 스크롤 (useEffect는 paint 후 실행 보장) //
  useEffect(() => {
    if (!isPanelMounted || !isPanelOpen || !FeedbackPanelRef.current) return;

    FeedbackPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [isPanelMounted]);

  function onPanelAnimationEnd() {
    if (!isPanelOpen) setIsPanelMounted(false);
  }

  // isThanksVisible true 시 DOM 마운트, false 시 fade-out 후 언마운트 //
  useEffect(() => {
    if (isThanksVisible) setIsThanksTextMounted(true);
  }, [isThanksVisible]);

  function onThanksAnimationEnd() {
    if (!isThanksVisible) setIsThanksTextMounted(false);
  }

  // 언마운트 시 타이머 정리 //
  useEffect(() => {
    return () => {
      if (ThanksTimerRef.current) clearTimeout(ThanksTimerRef.current);
      if (WobbleTimerRef.current) clearTimeout(WobbleTimerRef.current);
      if (ConfettiTimerRef.current) clearTimeout(ConfettiTimerRef.current);
    };
  }, []);

  // 감사 문구 2초 노출 //
  function showThanks() {
    setIsThanksVisible(true);

    if (ThanksTimerRef.current) clearTimeout(ThanksTimerRef.current);
    ThanksTimerRef.current = setTimeout(() => setIsThanksVisible(false), 2000);
  }

  // 좋아요 애니메이션 트리거 //
  function triggerLikeAnimation() {
    setIsWobbling(true);
    setConfetti(generateConfetti());

    if (WobbleTimerRef.current) clearTimeout(WobbleTimerRef.current);
    WobbleTimerRef.current = setTimeout(() => setIsWobbling(false), 700);

    if (ConfettiTimerRef.current) clearTimeout(ConfettiTimerRef.current);
    ConfettiTimerRef.current = setTimeout(() => setConfetti([]), 1050);
  }

  // 좋아요 클릭 — 즉시 저장, 패널 닫기 //
  function onClickThumbUp() {
    if (rating?.score === 1) return;

    onRate(1, null, null);
    setIsPanelOpen(false);
    showThanks();
    triggerLikeAnimation();
  }

  // 별로예요 클릭 — 즉시 저장 + 사유 패널 열기 (이미 부정 평가면 저장 없이 패널만 재오픈) //
  function onClickThumbDown() {
    if (rating?.score !== -1) onRate(-1, null, null);

    if (ThanksTimerRef.current) clearTimeout(ThanksTimerRef.current);
    setIsThanksVisible(false);

    setSelectedReason(rating?.score === -1 ? rating?.reason || null : null);
    setCommentValue(rating?.score === -1 ? rating?.comment || '' : '');
    setIsPanelOpen(true);
  }

  // 사유 칩 선택 (재클릭 시 해제) //
  function onClickReasonChip(reason) {
    setSelectedReason(prev => (prev === reason ? null : reason));
  }

  // 코멘트 입력 //
  function onChangeComment(e) {
    setCommentValue(e.target.value);
  }

  // 피드백 제출 — 사유/코멘트 포함 upsert //
  function onClickSubmitButton() {
    if (selectedReason === null && commentValue.trim() === '') return;

    onRate(-1, selectedReason, commentValue.trim() === '' ? null : commentValue.trim());
    setIsPanelOpen(false);
    showThanks();
  }

  // 패널 닫기 //
  function onClickCloseButton() {
    setIsPanelOpen(false);
  }

  // 엄지 아이콘 (active 시 채움 글리프, isDown이면 180도 회전) //
  function renderThumbIcon(isDown, isActive) {
    return (
      <ThumbSvg viewBox='0 0 24 24' $isDown={isDown} $color={isActive ? theme.primary : theme.subText}>
        {
          isActive ?
            <path d='M1 21h4V9H1v12zM23 10c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z' />
            :
            <path d='M9 21h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.58 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2zm0-12l4.34-4.34L12 10h9v2l-3 7H9V9zM1 9h4v12H1z' />
        }
      </ThumbSvg>
    );
  }

  const isSubmitDisabled = selectedReason === null && commentValue.trim() === '';

  return (
    <RatingWrapper>
      <IconRow>
        <ThumbUpWrapper>
          <IconButton
            onClick={onClickThumbUp}
            $hoverBg={theme.primaryAlpha10}
            $isWobbling={isWobbling}
            title={t('aiManager.rating.thumbUpTitle')}>
            {renderThumbIcon(false, rating?.score === 1)}
          </IconButton>
          {confetti.map(p => (
            <ConfettiPiece
              key={p.id}
              style={{
                left: p.x,
                top: p.y,
                width: p.width,
                height: p.height,
                backgroundColor: p.color,
                transform: `rotate(${p.rotation}deg)`,
                animationDelay: `${p.delay}s`,
              }}
            />
          ))}
        </ThumbUpWrapper>
        <IconButton
          onClick={onClickThumbDown}
          $hoverBg={theme.primaryAlpha10}
          title={t('aiManager.rating.thumbDownTitle')}>
          {renderThumbIcon(true, rating?.score === -1)}
        </IconButton>
        {
          isThanksTextMounted &&
          <ThanksText
            $isVisible={isThanksVisible}
            onAnimationEnd={onThanksAnimationEnd}
            color={theme.subText}>
            {t('aiManager.rating.thanks')}
          </ThanksText>
        }
      </IconRow>
      {
        isPanelMounted &&
        <FeedbackPanel
          ref={FeedbackPanelRef}
          $isOpen={isPanelOpen}
          onAnimationEnd={onPanelAnimationEnd}
          $bg={isDark ? theme.subBackground : theme.white}
          $border={isDark ? theme.background : '#ECECEC'}>
          <PanelHeader>
            <PanelTitle color={theme.text}>{t('aiManager.rating.panelTitle')}</PanelTitle>
            <CloseButton
              onClick={onClickCloseButton}
              color={theme.subText}>
              ✕
            </CloseButton>
          </PanelHeader>
          <ChipRow>
            {negativeReasons.map((reason) => (
              <ReasonChip
                key={'RatingReason-' + reason}
                onClick={() => onClickReasonChip(reason)}
                $isSelected={selectedReason === reason}
                $primary={theme.primary}
                $selectedBg={theme.primaryAlpha10}
                $border={theme.line}
                color={selectedReason === reason ? theme.primary : theme.subText}>
                {reason}
              </ReasonChip>
            ))}
          </ChipRow>
          <CommentInput
            rows={2}
            value={commentValue}
            onChange={onChangeComment}
            placeholder={t('aiManager.rating.commentPlaceholder')}
            color={theme.text}
            $placeholderColor={theme.subText}
            $bg={isDark ? theme.background : '#FAFAFA'} />
          <SubmitRow>
            <SubmitButton
              onClick={onClickSubmitButton}
              $disabled={isSubmitDisabled}
              $bg={theme.primary}
              color={theme.white}>
              {t('aiManager.rating.submit')}
            </SubmitButton>
          </SubmitRow>
        </FeedbackPanel>
      }
    </RatingWrapper>
  );
}

const thanksFadeIn = keyframes`
  from { opacity: 0; transform: translateX(-4px); }
  to   { opacity: 1; transform: translateX(0px); }
`;

const thanksFadeOut = keyframes`
  from { opacity: 1; transform: translateX(0px); }
  to   { opacity: 0; transform: translateX(-4px); }
`;

const panelSlideDown = keyframes`
  from { transform: translateY(-8px); opacity: 0; }
  to   { transform: translateY(0px);  opacity: 1; }
`;

const panelSlideUp = keyframes`
  from { transform: translateY(0px);  opacity: 1; }
  to   { transform: translateY(-8px); opacity: 0; }
`;

const thumbWobble = keyframes`
  0%   { transform: scale(1)    rotate(0deg); }
  15%  { transform: scale(1.45) rotate(-14deg); }
  30%  { transform: scale(1.4)  rotate(11deg); }
  50%  { transform: scale(1.25) rotate(-7deg); }
  68%  { transform: scale(1.12) rotate(4deg); }
  85%  { transform: scale(1.05) rotate(-1deg); }
  100% { transform: scale(1)    rotate(0deg); }
`;

const confettiFall = keyframes`
  0%   { transform: translateY(0px)   rotate(0deg);   opacity: 1; }
  70%  { opacity: 0.8; }
  100% { transform: translateY(48px)  rotate(480deg); opacity: 0; }
`;

const RatingWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  gap: 8px;
`;

const IconRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  gap: 2px;
`;

const ThumbUpWrapper = styled.div`
  position: relative;
`;

const IconButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 28px;
  height: 28px;

  border-radius: 6px;

  transition: background-color 0.2s ease-in-out;

  ${props => props.$isWobbling && css`
    animation: ${thumbWobble} 0.65s ease-in-out;
  `}

  &:hover {
    cursor: pointer;
    background-color: ${props => props.$hoverBg};
  }
`;

const ConfettiPiece = styled.div`
  position: absolute;
  border-radius: 1px;
  pointer-events: none;
  z-index: 10;
  animation: ${confettiFall} 0.95s ease-out forwards;
`;

const ThumbSvg = styled.svg`
  width: 16px;
  height: 16px;

  fill: ${props => props.$color};

  transform: ${props => props.$isDown ? 'rotate(180deg)' : 'none'};
`;

const ThanksText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 13px;

  color: ${props => props.color};

  margin: 0 0 0 6px;

  ${props => props.$isVisible
    ? css`animation: ${thanksFadeIn} 0.18s ease-out forwards;`
    : css`animation: ${thanksFadeOut} 0.18s ease-in forwards;`
  }
`;

const FeedbackPanel = styled.div`
  display: flex;
  flex-direction: column;

  padding: 12px;

  width: 100%;
  max-width: 420px;

  gap: 10px;

  border: 1px solid ${props => props.$border};
  border-radius: 10px;

  box-sizing: border-box;

  background-color: ${props => props.$bg};

  scroll-margin-bottom: 10px;

  ${props => props.$isOpen
    ? css`animation: ${panelSlideDown} 0.2s ease-out forwards;`
    : css`animation: ${panelSlideUp} 0.18s ease-in forwards;`
  }
`;

const PanelHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const PanelTitle = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.color};

  margin: 0;
`;

const CloseButton = styled.p`
  font-size: 13px;

  color: ${props => props.color};

  margin: 0;

  &:hover {
    cursor: pointer;
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  gap: 6px;
`;

const ReasonChip = styled.div`
  padding: 4px 10px;

  font-family: NotoSansKR-Regular;
  font-size: 13px;

  color: ${props => props.color};

  border: 1px solid ${props => props.$isSelected ? props.$primary : props.$border};
  border-radius: 15px;

  background-color: ${props => props.$isSelected ? props.$selectedBg : 'transparent'};

  transition: all 0.2s ease-in-out;

  &:hover {
    cursor: pointer;
    border-color: ${props => props.$primary};
  }
`;

const CommentInput = styled.textarea`
  padding: 8px 10px;

  width: 100%;

  font-family: NotoSansKR-Regular;
  font-size: 13px;
  line-height: 19px;

  color: ${props => props.color};

  border: none;
  border-radius: 8px;
  outline: none;
  resize: none;

  box-sizing: border-box;

  background-color: ${props => props.$bg};

  &::placeholder {
    color: ${props => props.$placeholderColor};
  }
`;

const SubmitRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;

const SubmitButton = styled.div`
  padding: 5px 14px;

  font-family: NotoSansKR-Medium;
  font-size: 13px;

  color: ${props => props.color};

  border-radius: 8px;

  background-color: ${props => props.$bg};

  opacity: ${props => props.$disabled ? 0.5 : 1};

  &:hover {
    cursor: ${props => props.$disabled ? 'default' : 'pointer'};
  }
`;
